import { useState, useEffect } from 'react';
import { useDeck } from '@/app/context/DeckContext/useDeck';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { StudyGuideOptions } from '@/app/api/StudyGuideAPI/StudyGuideAPI';

interface StudyGuideCustomizerProps {
  onGenerate: (options: StudyGuideOptions) => void;
  onCancel: () => void;
  isGenerating: boolean;
  suggestions: string[];
  onLoadSuggestions: (contentSource: 'all' | 'flagged') => void;
}

export const StudyGuideCustomizer = ({
  onGenerate,
  onCancel,
  isGenerating,
  suggestions,
  onLoadSuggestions
}: StudyGuideCustomizerProps) => {
  const { selectedDeck, flaggedCardIds } = useDeck();
  const { trackEvent, analyticsInstance } = useAnalytics();
  
  const [contentSource, setContentSource] = useState<'all' | 'flagged'>('all');
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);
  const [complexityLevel, setComplexityLevel] = useState<'novice' | 'intermediate' | 'expert'>('intermediate');
  const [studyGuideLength, setStudyGuideLength] = useState<'quick' | 'standard' | 'comprehensive'>('standard');

  useEffect(() => {
    if (suggestions.length > 0) {
      setIsLoadingSuggestions(false);
    } else {
      setIsLoadingSuggestions(true);
    }
  }, [suggestions]);

  // Reset form values when component mounts
  useEffect(() => {
    setSelectedSuggestions([]);
    setContentSource('all');
    setComplexityLevel('intermediate');
    setStudyGuideLength('standard');
    setCustomPrompt('');
  }, []);

  const handleContentSourceChange = (newContentSource: 'all' | 'flagged') => {
    setContentSource(newContentSource);
    setSelectedSuggestions([]); // Clear selected suggestions when switching
    setIsLoadingSuggestions(true); // Show loading state
    onLoadSuggestions(newContentSource); // Trigger suggestions reload
  };

  const handleSuggestionToggle = (suggestion: string) => {
    setSelectedSuggestions(prev => 
      prev.includes(suggestion)
        ? prev.filter(s => s !== suggestion)
        : [...prev, suggestion]
    );
  };

  const handleGenerate = () => {
    trackEvent(analyticsInstance, 'study_guide_customized', {
      deck_id: selectedDeck.id,
      content_source: contentSource,
      has_custom_prompt: customPrompt.length > 0,
      selected_suggestions_count: selectedSuggestions.length,
      flagged_cards_count: flaggedCardIds.length
    });

    onGenerate({
      contentSource,
      customPrompt,
      selectedSuggestions,
      complexityLevel,
      studyGuideLength
    });
  };

  const hasFlaggedCards = flaggedCardIds.length > 0;

  const SuggestionShimmer = () => (
    <div className="flex items-start space-x-3 p-3 rounded border border-base-300 animate-pulse">
      <div className="w-4 h-4 bg-base-300 rounded mt-0.5"></div>
      <div className="flex-1">
        <div className="h-4 bg-base-300 rounded w-3/4"></div>
      </div>
    </div>
  );

  return (
    <div className="flex justify-center">
      <div className="container max-w-4xl p-8">
        <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-base-content mb-2">
            Customize Your Study Guide
          </h2>
          <p className="text-base-content opacity-70">
            Personalize your study guide to focus on what matters most
          </p>
        </div>

        {/* Content Source Selection */}
        <div className="bg-base-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            What content should we focus on?
          </h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="contentSource"
                value="all"
                checked={contentSource === 'all'}
                onChange={(e) => handleContentSourceChange(e.target.value as 'all')}
                className="radio radio-primary"
              />
              <div>
                <div className="font-medium text-base-content">All content</div>
                <div className="text-sm text-base-content opacity-70">
                  Create a comprehensive study guide from your entire upload
                </div>
              </div>
            </label>
            
            <label 
              className={`flex items-center space-x-3 ${
                hasFlaggedCards ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
              }`}
            >
              <input
                type="radio"
                name="contentSource"
                value="flagged"
                checked={contentSource === 'flagged'}
                onChange={(e) => handleContentSourceChange(e.target.value as 'flagged')}
                disabled={!hasFlaggedCards}
                className="radio radio-primary"
              />
              <div>
                <div className="font-medium text-base-content">
                  Flagged cards only ({flaggedCardIds.length} cards)
                </div>
                <div className="text-sm text-base-content opacity-70">
                  {hasFlaggedCards 
                    ? 'Focus on concepts you\'ve marked for review'
                    : 'Flag some flashcards first to use this option'
                  }
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Complexity Level */}
        <div className="bg-base-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Choose complexity level
          </h3>
          <p className="text-sm text-base-content opacity-70 mb-4">
            Tailor the study guide to your current understanding level
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col items-center space-y-2 cursor-pointer p-4 rounded border border-base-300 hover:bg-base-300 transition-colors">
              <input
                type="radio"
                name="complexityLevel"
                value="novice"
                checked={complexityLevel === 'novice'}
                onChange={(e) => setComplexityLevel(e.target.value as 'novice')}
                className="radio radio-primary"
              />
              <div className="text-center">
                <div className="font-semibold text-base-content">Novice</div>
                <div className="text-xs text-base-content opacity-70">
                  Simple explanations, basic concepts
                </div>
              </div>
            </label>
            
            <label className="flex flex-col items-center space-y-2 cursor-pointer p-4 rounded border border-base-300 hover:bg-base-300 transition-colors">
              <input
                type="radio"
                name="complexityLevel"
                value="intermediate"
                checked={complexityLevel === 'intermediate'}
                onChange={(e) => setComplexityLevel(e.target.value as 'intermediate')}
                className="radio radio-primary"
              />
              <div className="text-center">
                <div className="font-semibold text-base-content">Intermediate</div>
                <div className="text-xs text-base-content opacity-70">
                  Balanced detail with examples
                </div>
              </div>
            </label>
            
            <label className="flex flex-col items-center space-y-2 cursor-pointer p-4 rounded border border-base-300 hover:bg-base-300 transition-colors">
              <input
                type="radio"
                name="complexityLevel"
                value="expert"
                checked={complexityLevel === 'expert'}
                onChange={(e) => setComplexityLevel(e.target.value as 'expert')}
                className="radio radio-primary"
              />
              <div className="text-center">
                <div className="font-semibold text-base-content">Expert</div>
                <div className="text-xs text-base-content opacity-70">
                  Advanced concepts, technical depth
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Study Guide Length */}
        <div className="bg-base-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Study guide length
          </h3>
          <p className="text-sm text-base-content opacity-70 mb-4">
            Choose how detailed and extensive you want your study guide to be
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col items-center space-y-2 cursor-pointer p-4 rounded border border-base-300 hover:bg-base-300 transition-colors">
              <input
                type="radio"
                name="studyGuideLength"
                value="quick"
                checked={studyGuideLength === 'quick'}
                onChange={(e) => setStudyGuideLength(e.target.value as 'quick')}
                className="radio radio-primary"
              />
              <div className="text-center">
                <div className="font-semibold text-base-content">Quick Review</div>
                <div className="text-xs text-base-content opacity-70">
                  Key points & essential summary
                </div>
              </div>
            </label>
            
            <label className="flex flex-col items-center space-y-2 cursor-pointer p-4 rounded border border-base-300 hover:bg-base-300 transition-colors">
              <input
                type="radio"
                name="studyGuideLength"
                value="standard"
                checked={studyGuideLength === 'standard'}
                onChange={(e) => setStudyGuideLength(e.target.value as 'standard')}
                className="radio radio-primary"
              />
              <div className="text-center">
                <div className="font-semibold text-base-content">Standard</div>
                <div className="text-xs text-base-content opacity-70">
                  Balanced coverage with examples
                </div>
              </div>
            </label>
            
            <label className="flex flex-col items-center space-y-2 cursor-pointer p-4 rounded border border-base-300 hover:bg-base-300 transition-colors">
              <input
                type="radio"
                name="studyGuideLength"
                value="comprehensive"
                checked={studyGuideLength === 'comprehensive'}
                onChange={(e) => setStudyGuideLength(e.target.value as 'comprehensive')}
                className="radio radio-primary"
              />
              <div className="text-center">
                <div className="font-semibold text-base-content">Comprehensive</div>
                <div className="text-xs text-base-content opacity-70">
                  Thorough in-depth coverage
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* AI Suggestions */}
        <div className="bg-base-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Select focus areas (optional)
          </h3>
          <p className="text-sm text-base-content opacity-70 mb-4">
            Create a more targeted study guide by selecting specific topic areas:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isLoadingSuggestions ? (
              // Show shimmer placeholders
              Array.from({ length: 6 }).map((_, index) => (
                <SuggestionShimmer key={index} />
              ))
            ) : (
              // Show actual suggestions
              suggestions.map((suggestion, index) => (
                <label
                  key={index}
                  className="flex items-start space-x-3 cursor-pointer p-3 rounded border border-base-300 hover:bg-base-300 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedSuggestions.includes(suggestion)}
                    onChange={() => handleSuggestionToggle(suggestion)}
                    className="checkbox checkbox-primary mt-0.5"
                  />
                  <span className="text-sm text-base-content">{suggestion}</span>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Custom Prompt */}
        <div className="bg-base-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Additional instructions (optional)
          </h3>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Tell us how you'd like your study guide customized... 
Examples:
• Focus on practical applications
• Include more examples and analogies  
• Create practice questions for each topic
• Organize by difficulty level"
            className="textarea textarea-bordered w-full h-32 text-base-content placeholder-base-content placeholder-opacity-50"
            maxLength={500}
          />
          <div className="text-right text-xs text-base-content opacity-70 mt-1">
            {customPrompt.length}/500
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 pt-4">
          <button
            onClick={onCancel}
            disabled={isGenerating}
            className="btn btn-ghost text-base-content"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="btn btn-primary"
          >
            {isGenerating ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Generating...
              </>
            ) : (
              'Generate Study Guide'
            )}
          </button>
        </div>
      </div>
    </div>
    </div>
  );
};